import { useState } from "react";
import { Wrench, Layers, UserRoundCog, Trash2, ShieldAlert, Loader2 } from "lucide-react";
import adminApi from "@/lib/adminApi";
import { toast } from "sonner";

export default function AdminTools() {
  return (
    <div className="p-6 md:p-8 max-w-4xl mx-auto" data-testid="admin-tools">
      <div className="mb-6">
        <h1 className="font-heading text-3xl font-bold flex items-center gap-2">
          <Wrench className="w-6 h-6 text-kudi-terracotta" /> Admin tools
        </h1>
        <p className="text-sm text-kudi-ink/60 mt-1">Data-integrity utilities. Handle with care — these are destructive.</p>
      </div>

      <div className="space-y-4">
        <DedupeInventoryCard />
        <WalletBackfillCard />
        <MergeAccountsCard />
        <PurgeTestDataCard />
      </div>
    </div>
  );
}

function WalletBackfillCard() {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!window.confirm("Seed wallet_in entries from all historical payment_received transactions? Safe & idempotent — re-runs skip duplicates.")) return;
    setRunning(true);
    setResult(null);
    try {
      const r = await adminApi.post("/wallet/backfill", {});
      setResult(r.data);
      toast.success(`Backfill complete — seeded ${r.data.seeded_wallet_in}, skipped ${r.data.skipped_dup} duplicates`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Backfill failed");
    } finally { setRunning(false); }
  };

  return (
    <Card icon={Layers} title="Wallet backfill" description="One-time migration: creates a wallet credit for every historical payment_received transaction. Fixes users whose wallet balance is short of what they've actually received. Idempotent." tone="indigo">
      <button onClick={run} disabled={running} className="text-xs bg-indigo-600 text-white px-3 py-2 rounded-lg hover:bg-indigo-700 transition disabled:opacity-50 inline-flex items-center gap-1.5" data-testid="run-wallet-backfill">
        {running ? <Loader2 className="w-3 h-3 animate-spin" /> : <Layers className="w-3 h-3" />}
        {running ? "Running…" : "Run backfill"}
      </button>
      {result && (
        <div className="mt-3 text-xs bg-white border border-indigo-100 rounded-lg p-3 space-y-0.5">
          <div>Seeded wallet_in entries: <b>{result.seeded_wallet_in}</b></div>
          <div>Skipped (already migrated): <b>{result.skipped_dup}</b></div>
        </div>
      )}
    </Card>
  );
}

function DedupeInventoryCard() {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!window.confirm("Run inventory dedupe across ALL users? This merges case-insensitive duplicate items (e.g. 'Garri' + 'garri' → one row).")) return;
    setRunning(true);
    setResult(null);
    try {
      const r = await adminApi.post("/admin/inventory/dedupe", {});
      setResult(r.data);
      toast.success(`Dedupe done — ${r.data.rows_merged_into} merges, ${r.data.rows_deleted} deletes`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Dedupe failed");
    } finally { setRunning(false); }
  };

  return (
    <Card icon={Layers} title="Inventory dedupe" description="Merges case-insensitive duplicate inventory rows for every user (e.g. 'Garri' + 'garri' + 'GARRI' collapse into one). Safe & idempotent." tone="indigo">
      <button onClick={run} disabled={running} className="text-xs bg-indigo-600 text-white px-3 py-2 rounded-lg hover:bg-indigo-700 transition disabled:opacity-50 inline-flex items-center gap-1.5" data-testid="run-dedupe">
        {running ? <Loader2 className="w-3 h-3 animate-spin" /> : <Layers className="w-3 h-3" />}
        {running ? "Running…" : "Run dedupe"}
      </button>
      {result && (
        <div className="mt-3 text-xs bg-white border border-indigo-100 rounded-lg p-3 space-y-0.5">
          <div>Users scanned: <b>{result.users_scanned}</b></div>
          <div>Users with dupes: <b>{result.users_with_dupes}</b></div>
          <div>Rows merged into: <b>{result.rows_merged_into}</b></div>
          <div>Rows deleted: <b>{result.rows_deleted}</b></div>
        </div>
      )}
    </Card>
  );
}

function MergeAccountsCard() {
  const [primary, setPrimary] = useState("");
  const [secondary, setSecondary] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!primary || !secondary) return toast.error("Enter both emails");
    if (primary.trim().toLowerCase() === secondary.trim().toLowerCase()) return toast.error("Emails must be different");
    if (!window.confirm(`Merge ALL data from ${secondary} into ${primary}? The secondary account will be deleted.`)) return;
    setRunning(true);
    setResult(null);
    try {
      const r = await adminApi.post("/admin/merge-accounts", { primary_email: primary.trim().toLowerCase(), secondary_email: secondary.trim().toLowerCase() });
      setResult(r.data);
      toast.success("Merge complete");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Merge failed");
    } finally { setRunning(false); }
  };

  return (
    <Card icon={UserRoundCog} title="Merge accounts" description="Consolidates a user's WhatsApp-created account and their web-signup account into one. Transactions, inventory, chat, wallet all move to the primary." tone="amber">
      <div className="space-y-2">
        <label className="block text-xs">
          <span className="text-kudi-ink/70">Primary email (kept):</span>
          <input type="email" value={primary} onChange={(e) => setPrimary(e.target.value)}
            className="mt-0.5 w-full border border-border rounded-lg px-2 py-1.5 text-sm focus:border-amber-500 focus:outline-none"
            placeholder="trader@email.com" data-testid="merge-primary" />
        </label>
        <label className="block text-xs">
          <span className="text-kudi-ink/70">Secondary email (deleted after merge):</span>
          <input type="email" value={secondary} onChange={(e) => setSecondary(e.target.value)}
            className="mt-0.5 w-full border border-border rounded-lg px-2 py-1.5 text-sm focus:border-amber-500 focus:outline-none"
            placeholder="other@email.com" data-testid="merge-secondary" />
        </label>
        <button onClick={run} disabled={running || !primary || !secondary}
          className="text-xs bg-amber-600 text-white px-3 py-2 rounded-lg hover:bg-amber-700 transition disabled:opacity-50 inline-flex items-center gap-1.5" data-testid="run-merge">
          {running ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserRoundCog className="w-3 h-3" />}
          {running ? "Merging…" : "Merge accounts"}
        </button>
        {result && (
          <div className="text-xs bg-white border border-amber-100 rounded-lg p-3">
            <pre className="whitespace-pre-wrap">{JSON.stringify(result, null, 2)}</pre>
          </div>
        )}
      </div>
    </Card>
  );
}

function PurgeTestDataCard() {
  const [keepEmails, setKeepEmails] = useState("admin@kobolog.ai, adaclement58@gmail.com");
  const [confirm, setConfirm] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (confirm !== "PURGE_NOW") return toast.error("Type PURGE_NOW to confirm");
    if (!window.confirm("This will DELETE all users NOT in the keep list, plus their transactions, inventory, loans and chat history. Continue?")) return;
    setRunning(true);
    setResult(null);
    try {
      const emails = keepEmails.split(",").map((e) => e.trim()).filter(Boolean);
      const r = await adminApi.post("/admin/purge-test-data", { keep_emails: emails, keep_phones: [], confirm: "PURGE_NOW" });
      setResult(r.data);
      setConfirm("");
      toast.success("Purge complete");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Purge failed");
    } finally { setRunning(false); }
  };

  return (
    <Card icon={Trash2} title="Purge test data" description="⚠ DESTRUCTIVE. Deletes every user (and their transactions/inventory/loans/chat/payments) EXCEPT the emails you keep. Use for cleaning demo data. Type PURGE_NOW to confirm." tone="rose">
      <div className="space-y-2">
        <label className="block text-xs">
          <span className="text-kudi-ink/70">Emails to keep (comma-separated):</span>
          <input type="text" value={keepEmails} onChange={(e) => setKeepEmails(e.target.value)}
            className="mt-0.5 w-full border border-border rounded-lg px-2 py-1.5 text-sm font-mono focus:border-rose-500 focus:outline-none" data-testid="purge-keep-emails" />
        </label>
        <label className="block text-xs">
          <span className="text-kudi-ink/70">Confirmation (type PURGE_NOW):</span>
          <input type="text" value={confirm} onChange={(e) => setConfirm(e.target.value)}
            className="mt-0.5 w-full border border-border rounded-lg px-2 py-1.5 text-sm font-mono focus:border-rose-500 focus:outline-none" data-testid="purge-confirm" />
        </label>
        <button onClick={run} disabled={running || confirm !== "PURGE_NOW"}
          className="text-xs bg-rose-600 text-white px-3 py-2 rounded-lg hover:bg-rose-700 transition disabled:opacity-50 inline-flex items-center gap-1.5" data-testid="run-purge">
          {running ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShieldAlert className="w-3 h-3" />}
          {running ? "Purging…" : "Purge test data"}
        </button>
        {result && (
          <div className="text-xs bg-white border border-rose-100 rounded-lg p-3">
            <pre className="whitespace-pre-wrap">{JSON.stringify(result, null, 2)}</pre>
          </div>
        )}
      </div>
    </Card>
  );
}

const TONE = {
  indigo: "bg-indigo-50 border-indigo-200",
  amber: "bg-amber-50 border-amber-200",
  rose: "bg-rose-50 border-rose-200",
};

function Card({ icon: Icon, title, description, tone = "indigo", children }) {
  return (
    <div className={`rounded-2xl border p-4 ${TONE[tone]}`}>
      <div className="flex items-start gap-3 mb-3">
        <Icon className="w-5 h-5 mt-0.5 shrink-0" />
        <div>
          <h2 className="font-heading font-bold text-base">{title}</h2>
          <p className="text-xs text-kudi-ink/70 mt-0.5">{description}</p>
        </div>
      </div>
      {children}
    </div>
  );
}
